import { gql, useQuery } from '@apollo/client';
import { Loading } from '@perch-labs/shrike';
import { useState } from 'react';
import { ValidatingInput, InputComponentProps, OnChange } from '~/components/validating-input';
import {
  Flex_FetchUtilityAccountNumberRulesQuery,
  Flex_FetchUtilityAccountNumberRulesQueryVariables,
} from '~/typings/graphql';
import { normalizeMnUtilityAccountNumber } from '~/utils/normalize-mn-utility-account-number';

export const FETCH_ACCOUNT_NUMBER_RULES = gql`
  query Flex_FetchUtilityAccountNumberRules($utilityId: Int!) {
    utility(id: $utilityId) {
      rules: accountNumberRules {
        example
        pattern
      }
    }
  }
`;

interface UtilityAccountNumberProps extends InputComponentProps {
  hideRequirements?: boolean;
  utilityId?: number;
  utilityName?: string;
  label?: string; // this is getting used as an override in the spread, TODO: make it more explicit
  /** Temporary: collapse MN trailing check-digit hyphen before validate/save. */
  normalizeMnCheckDigitHyphen?: boolean;
}

export const UtilityAccountNumber = ({
  hideRequirements,
  utilityId,
  utilityName,
  normalizeMnCheckDigitHyphen,
  onChange,
  value,
  ...props
}: UtilityAccountNumberProps) => {
  const [inputEpoch, setInputEpoch] = useState(0);
  const [forcedValue, setForcedValue] = useState<string | undefined>(undefined);
  const { data, loading } = useQuery<
    Flex_FetchUtilityAccountNumberRulesQuery,
    Flex_FetchUtilityAccountNumberRulesQueryVariables
  >(FETCH_ACCOUNT_NUMBER_RULES, {
    fetchPolicy: 'no-cache',
    skip: !utilityId,
    // @ts-expect-error — undefined input handled by skip param
    variables: { utilityId },
  });

  if (loading) return <Loading />;

  const rules = data?.utility?.rules || [];
  const examples = rules
    .map(rule => rule?.example)
    .filter((example): example is string => !!example);
  const patterns = rules.map(rule => rule?.pattern);
  const requirements = examples.length ? `Example: ${examples.join(', ')}` : '';
  const label = utilityName ? `Account number for ${utilityName}` : 'Account number';

  const maybeNormalize = (raw: string) =>
    normalizeMnCheckDigitHyphen ? normalizeMnUtilityAccountNumber(raw) : raw;

  const handleChange: OnChange = (next, meta) => {
    if (typeof next === 'string' && normalizeMnCheckDigitHyphen) {
      const collapsed = normalizeMnUtilityAccountNumber(next);
      if (collapsed !== next) {
        setForcedValue(collapsed);
        setInputEpoch(epoch => epoch + 1);
        onChange(collapsed, meta);
        return;
      }
    }
    onChange(next, meta);
  };

  const validate = ({ value: rawValue }) => {
    if (!rawValue) return false;
    if (!rules.length) return true;
    const valueToTest = maybeNormalize(rawValue as string);
    const isValid = !!patterns.find(pattern => new RegExp(pattern!).test(valueToTest));

    // check for duplicate account number elsewhere on the page (by name)
    const els = Array.from(
      document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
        'input[name="utilityAccountNumber"]',
      ),
    );
    // normalize by removing non-digits (ignores dashes, spaces, etc.)
    const normalize = (s?: string) => (s || '').replace(/\D/g, '');
    const normalizedValue = normalize(valueToTest);
    const duplicates = els.filter(el => normalize(el.value) === normalizedValue);
    if (duplicates.length > 1) {
      return <span>Duplicate utility account number.</span>;
    }
    if (!isValid) {
      /**
       * TODO: this is for showing red highlight around the
       * input box. Remove this when proper error reporting
       * is done.
       */
      return <span>Please enter a valid account number.</span>;
    }
    return isValid;
  };

  return (
    <ValidatingInput
      key={inputEpoch}
      debounceValidation={0}
      disabled={loading}
      label={label}
      name="utilityAccountNumber"
      requirements={hideRequirements ? '' : requirements}
      validate={validate}
      {...props}
      value={forcedValue !== undefined ? forcedValue : value}
      onChange={handleChange}
      helpContent={{
        type: 'ENROLLMENT_FIELDS',
      }}
    />
  );
};
